import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus } from "lucide-react";
import { useCart } from "../cart";
import CheckoutForm from "./CheckoutForm";

const CartDrawer = () => {
  const { items, open, setOpen, remove, updateQty, total } = useCart();
  const [showForm, setShowForm] = useState(false);

  const goToCheckout = () => {
    if (!items.length) return;
    setShowForm(true);
  };

  const closeAll = () => {
    setShowForm(false);
    setOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && !showForm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-[#1A1612]/30 backdrop-blur-sm z-[60]"
              data-testid="cart-overlay"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
              className="fixed top-0 right-0 bottom-0 w-full sm:w-[460px] bg-[#FAF5EC] border-l border-[#E5DDD0] z-[70] flex flex-col"
              data-testid="cart-drawer"
            >
              <header className="flex items-center justify-between p-8 border-b border-[#E5DDD0]">
                <h3 className="font-serif text-2xl text-[#1A1612] tracking-wider">Coșul tău</h3>
                <button
                  onClick={() => setOpen(false)}
                  className="text-[#6E665A] hover:text-[#1A1612] transition"
                  data-testid="cart-close-button"
                  aria-label="Close cart"
                >
                  <X size={22} strokeWidth={1.4} />
                </button>
              </header>

              <div className="flex-1 overflow-y-auto p-8">
                {items.length === 0 ? (
                  <p className="text-center text-[#6E665A] mt-20 font-light" data-testid="cart-empty">
                    Coșul este gol.
                  </p>
                ) : (
                  <ul className="space-y-8">
                    {items.map((it) => (
                      <li key={it.id} className="flex gap-4" data-testid={`cart-item-${it.id}`}>
                        <div className="w-20 h-24 bg-[#F4ECDD] overflow-hidden flex-shrink-0 rounded-lg">
                          <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-serif text-lg text-[#1A1612] mb-1 truncate">{it.name}</h4>
                          <p className="text-xs uppercase tracking-[0.2em] text-[#B8954C] mb-3">
                            {Number(it.price).toFixed(0)} RON
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 border border-[#E5DDD0] rounded-full">
                              <button
                                onClick={() => updateQty(it.id, it.qty - 1)}
                                className="w-8 h-8 flex items-center justify-center text-[#6E665A] hover:text-[#1A1612] transition"
                                data-testid={`cart-qty-decrease-${it.id}`}
                              >
                                <Minus size={14} />
                              </button>
                              <span className="text-sm text-[#1A1612] w-6 text-center" data-testid={`cart-qty-${it.id}`}>
                                {it.qty}
                              </span>
                              <button
                                onClick={() => updateQty(it.id, it.qty + 1)}
                                className="w-8 h-8 flex items-center justify-center text-[#6E665A] hover:text-[#1A1612] transition"
                                data-testid={`cart-qty-increase-${it.id}`}
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                            <button
                              onClick={() => remove(it.id)}
                              className="text-xs text-[#6E665A] hover:text-[#1A1612] transition"
                              data-testid={`cart-remove-${it.id}`}
                            >
                              Elimină
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {items.length > 0 && (
                <footer className="p-8 border-t border-[#E5DDD0] space-y-6">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-[0.3em] text-[#6E665A]">Total</span>
                    <span className="font-serif text-3xl text-[#1A1612]" data-testid="cart-total">
                      {total.toFixed(0)} <span className="text-[#B8954C] text-xs uppercase tracking-[0.2em]">RON</span>
                    </span>
                  </div>
                  <button
                    onClick={goToCheckout}
                    className="btn-pill w-full"
                    data-testid="cart-checkout-button"
                  >
                    Finalizează Comanda
                  </button>
                </footer>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <CheckoutForm open={showForm} onBack={() => setShowForm(false)} onClose={closeAll} />
    </>
  );
};

export default CartDrawer;
